import { InfoCircleOutlined } from '@ant-design/icons';
import { Space, Table, Tooltip } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { isEmpty, isNil, pick, sortBy, uniqBy, uniqWith } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import { type InventoryDetailsDataFragment } from '../../../api/fragments/InventoryDetailsData';
import { type InventoryListDataFragment } from '../../../api/fragments/InventoryListData';
import { type StockInventorySpecsFragment } from '../../../api/fragments/StockInventorySpecs';
import { useListInventoriesFilterQuery } from '../../../api/queries';
import { useListInventoriesQuery } from '../../../api/queries/listInventories';
import { useListLocalModelsQuery } from '../../../api/queries/listLocalModels';
import { CompanyFilterListCollection } from '../../../api/types';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import NotFoundResult from '../../../components/results/NotFoundResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import hasPermissions from '../../../utilities/hasPermissions';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import useActiveFilter from '../../../utilities/useActiveFilter';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useTranslatedString from '../../../utilities/useTranslatedString';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const filterListMapper = list =>
    list?.map(model => ({
        text: model.name.defaultValue,
        value: model.id,
    })) || [];

const InventoryList = () => {
    const { t } = useTranslation('inventoryList');

    const { permissions: accountPermissions } = useAccount();
    const translate = useTranslatedString();

    const searchBox = useSearchInDropDown();

    // get company context
    const company = useCompany(true);

    const { dealerIds } = useMultipleDealerIds();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Inventory);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    const navigate = useNavigate();

    // fetch model data for filter
    const { data: allModelAndSubmodelData } = useListLocalModelsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });

    const { data: filterList } = useListInventoriesFilterQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });

    const modelFilter = useMemo(
        () =>
            sortBy(
                'text',
                filterListMapper(allModelAndSubmodelData?.list?.items.filter(vehicle => isEmpty(vehicle.parentModel)))
            ),
        [allModelAndSubmodelData?.list?.items]
    );

    const submodelFilter = useMemo(
        () =>
            sortBy(
                'text',
                filterListMapper(allModelAndSubmodelData?.list?.items.filter(vehicle => !isEmpty(vehicle.parentModel)))
            ),
        [allModelAndSubmodelData?.list?.items]
    );

    // fetch data
    const { page, pageSize, sort, filter } = state;

    const { data, loading, error } = useListInventoriesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { dealerIds, companyIds: company ? [company.id] : undefined, ...filter },
        },
    });

    const filterTypeList = filterList?.listInventoriesFilter || [];

    const [moduleList, variantList, colorList, trimList, packageList] = useMemo(() => {
        const modules = filterTypeList.flatMap(item => item.module);
        const variants = filterTypeList
            .flatMap(item => item.variant)
            .map(item => ({ ...item, variantName: translate(item.name) }));
        const colors = filterTypeList
            .flatMap(item => item.__typename === 'ConfiguratorInventory' && item.colorSetting)
            .map(item => ({ ...item, colorName: translate(item.name) }))
            .filter(item => !isNil(item.id) && !isEmpty(item.id));

        const trims = filterTypeList
            .flatMap(item => item.__typename === 'ConfiguratorInventory' && item.trimSetting)
            .map(item => ({ ...item, trimName: translate(item.name) }))
            .filter(item => !isNil(item.id) && !isEmpty(item.id));

        const packages = filterTypeList
            .flatMap(item => item.__typename === 'ConfiguratorInventory' && item.packageSetting)
            .map(item => ({ ...item, packageTranslatedName: translate(item?.packageName) }))
            .filter(item => !isNil(item.id) && (!isEmpty(item.id) || !isEmpty(item.packageName)));

        const moduleList = uniqBy('displayName', modules).map(module => ({
            text: module.displayName,
            value: module.displayName,
        }));
        const variantList = uniqWith(
            (a, b) => a.text.toLowerCase() === b.text.toLowerCase(),
            uniqBy('variantName', variants).map(variant => ({
                text: variant.variantName,
                value: variant.variantName,
            }))
        );
        const colorList = uniqWith(
            (a, b) => a.text.toLowerCase() === b.text.toLowerCase(),
            uniqBy('colorName', colors).map(color => ({ text: color.colorName, value: color.colorName }))
        );

        const trimList = uniqWith(
            (a, b) => a.text.toLowerCase() === b.text.toLowerCase(),
            uniqBy('trimName', trims).map(trim => ({ text: trim.trimName, value: trim.trimName }))
        );
        const packageList = uniqWith(
            (a, b) => a.text.toLowerCase() === b.text.toLowerCase(),
            uniqBy('packageTranslatedName', packages).map(packageOption => ({
                text: packageOption?.packageTranslatedName,
                value: packageOption?.packageTranslatedName,
            }))
        );

        return [moduleList, variantList, colorList, trimList, packageList];
    }, [uniqBy, filterTypeList]);

    const companyFilterList = useGetCompanyFilter({ dealerIds, collection: CompanyFilterListCollection.Inventories });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            variantName: !filters.variantName ? null : filters.variantName,
                            colorSettingName: !filters.colorSettingName ? null : filters.colorSettingName,
                            trimSettingName: !filters.trimSettingName ? null : filters.trimSettingName,
                            packageSettingName: !filters.packageSettingName ? null : filters.packageSettingName,
                            vin: !filters.vin ? null : filters.vin[0],
                            actives: !filters.isActive ? [] : filters.isActive,
                            moduleName: !filters.moduleName ? null : filters.moduleName,
                            modelIds: !filters.model ? [] : filters.model,
                            submodelIds: !filters.subModel ? [] : filters.subModel,
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    // prepare items as a data source
    const dataSource = useMemo(() => data?.list?.items || [], [data]);

    const total = data?.list?.count || 0;

    const {
        companySort,
        moduleSort,
        modelNameSort,
        subModelSort,
        variantNameSort,
        configuratorColorNameSort,
        configuratorTrimNameSort,
        configuratorPackageNameSort,
        assignedVinCountSort,
    } = useSortOrder(sort);

    const activeFilter = useActiveFilter();

    if (!loading && error) {
        return <InternalErrorResult />;
    }

    if (!hasPermissions(accountPermissions, [permissionKind.viewInventories])) {
        return <NotFoundResult />;
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: InventoryDetailsDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/inventories/${row.id}`, { state: { currentPage: 'inventoryList' } });
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('inventoryList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={moduleSort}
                    title={t('inventoryList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="dealer"
                dataIndex={['dealer', 'displayName']}
                title={t('inventoryList:columns.dealer')}
            />
            <Table.Column
                key="moduleName"
                dataIndex={['module', 'displayName']}
                {...(moduleList.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, moduleList),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.moduleName ? filter.moduleName : undefined,
                })}
                sortOrder={companySort}
                title={t('inventoryList:columns.module')}
                sorter
            />
            <Table.Column
                key="model"
                dataIndex={['model', 'name']}
                {...(modelFilter.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, modelFilter),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.modelIds,
                })}
                render={value => translate(value)}
                sortOrder={modelNameSort}
                title={t('inventoryList:columns.model')}
                sorter
            />
            <Table.Column
                key="subModel"
                {...(submodelFilter.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, submodelFilter),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.submodelIds,
                })}
                dataIndex={['subModel', 'name']}
                render={value => (value ? translate(value) : '-')}
                sortOrder={subModelSort}
                title={t('inventoryList:columns.subModel')}
                sorter
            />
            <Table.Column
                key="variantName"
                dataIndex={['variant', 'name']}
                {...(variantList.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, variantList),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.variantName ? [...filter.variantName] : undefined,
                })}
                render={value => translate(value)}
                sortOrder={variantNameSort}
                title={t('inventoryList:columns.variant')}
            />
            <Table.Column
                key="colorSettingName"
                dataIndex={['colorSetting', 'name', 'defaultValue']}
                {...(colorList.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, colorList),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.colorSettingName ? [...filter.colorSettingName] : undefined,
                })}
                render={value => value ?? '-'}
                sortOrder={configuratorColorNameSort}
                title={t('inventoryList:columns.color')}
                sorter
            />
            <Table.Column
                key="trimSettingName"
                dataIndex={['trimSetting', 'name', 'defaultValue']}
                {...(trimList.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, trimList),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.trimSettingName ? [...filter.trimSettingName] : undefined,
                })}
                render={value => value ?? '-'}
                sortOrder={configuratorTrimNameSort}
                title={t('inventoryList:columns.trim')}
                sorter
            />
            <Table.Column
                key="packageSettingName"
                dataIndex={['packageSetting', 'packageName', 'defaultValue']}
                {...(packageList.length > 1 && {
                    filterDropdown: props => showFilterDropDown(props, packageList),
                    filterIcon: renderFilterIcon,
                    filteredValue: filter.packageSettingName ? [...filter.packageSettingName] : undefined,
                })}
                render={value => value ?? '-'}
                sortOrder={configuratorPackageNameSort}
                title={t('inventoryList:columns.package')}
                sorter
            />
            <Table.Column
                key="assignedVinCount"
                dataIndex="assignedVinCount"
                render={value => value ?? '-'}
                sortOrder={assignedVinCountSort}
                // for sake of sort
                title={t('inventoryList:columns.vin')}
                sorter
            />
            <Table.Column
                key="stock"
                dataIndex="stocks"
                render={(stocks: StockInventorySpecsFragment[], record: InventoryListDataFragment) => (
                    <span>{`${record.remainingStock} (${record.reservedStock}) / ${stocks.length}`}</span>
                )}
                title={
                    <Space>
                        <span>{t('inventoryList:columns.stock')}</span>
                        <Tooltip title={t('inventoryList:columns.stockTooltip')}>
                            <InfoCircleOutlined />
                        </Tooltip>
                    </Space>
                }
                // sorter
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                filterDropdown={props => showFilterDropDown(props, activeFilter)}
                filterIcon={renderFilterIcon}
                filteredValue={filter?.actives}
                render={value => renderBooleanIcon(value, true)}
                title={t('inventoryList:columns.isActive')}
                width={120}
            />
        </PaginatedTableWithContext>
    );
};

export default InventoryList;
